import { useCallback, useEffect, useRef, useState } from 'react'

import useTranslations from '../../../../../hooks/useTranslations'
import ANTDButton from '../../../../../shared/antd/ANTDButton'
import ANTDDivider from '../../../../../shared/antd/ANTDDivider'
import {
  useFormInstanceFn,
  useWatchFn,
} from '../../../../../shared/antd/ANTDForm'
import ANTDInput from '../../../../../shared/antd/ANTDInput'
import ANTDSelect from '../../../../../shared/antd/ANTDSelect'
import ANTDSpin from '../../../../../shared/antd/ANTDSpin'
import debounce from '../../../../../utils/debounce'
import { isEqual, notEqual } from '../../../../../utils/javascript'
import {
  getVehicleModelListApi,
  searchVehicleModelListApi,
} from '../../../jobs.api'

const VehicleModelSelector = ({
  producerId,
  fieldName,
  addOption = null,
  onModelSelect,
  ...restProps
}) => {
  const form = useFormInstanceFn()
  // eslint-disable-next-line no-unused-vars
  const selectedOption = useWatchFn(fieldName, form)
  const labelKeyFieldName = [...fieldName?.slice(0, -1), 'modelKey']
  const { t } = useTranslations()
  const [vehicleModelData, setVehicleModelData] = useState({ list: [] })
  const [search, setSearch] = useState('')

  const { list = [], loader } = vehicleModelData || {}
  const PAGE_SIZE = 10
  const dataRef = useRef({ vehicleModelData, search })

  useEffect(() => {
    setSearch('')
    getVehicleModelList({})
  }, [producerId])

  const getVehicleModelList = async ({
    pageNo = 1,
    search = '',
    newValue = null,
  }) => {
    setVehicleModelData(prev => ({ ...prev, loader: true }))

    let resp
    const payload = {
      pageSize: PAGE_SIZE,
      ...(producerId && { producerId }),
    }
    if (search?.trim()) {
      payload.search = search?.trim()
      resp = await searchVehicleModelListApi({
        params: payload,
        pageNo,
      })
    } else {
      resp = await getVehicleModelListApi({
        params: payload,
        pageNo,
      })
    }

    const combinedList = [
      ...(notEqual(pageNo, 1) ? vehicleModelData?.list : []),
      ...(resp?.data?.list || []),
    ]
    if (newValue) {
      const newValueIncluded = combinedList.find(
        item => item?.id === newValue?.id,
      )
      if (!newValueIncluded) {
        combinedList.unshift(newValue)
      }
    }
    const modelList = [
      ...new Map(combinedList.map(item => [item.id, item])).values(),
    ]
    setVehicleModelData(prev => ({
      ...resp?.data,
      list: modelList,
      loader: false,
    }))
  }

  dataRef.current = { vehicleModelData, search, getVehicleModelList }

  const debouncedGetVehicleModelList = useCallback(
    debounce(getVehicleModelList, 400),
    [producerId],
  )

  const onInputSearch = async e => {
    const { value } = e?.target
    setSearch(value)

    debouncedGetVehicleModelList({ search: value })
  }

  const SCROLL_THRESHOLD = 10
  const onScroll = useCallback(
    debounce(e => {
      const { target } = e
      const {
        vehicleModelData: data,
        search: searchVal,
        getVehicleModelList: fetchList,
      } = dataRef.current
      const { pageNo, hasMore } = data || {}
      const scrollBottom = target.scrollTop + target.clientHeight
      const nearBottom = scrollBottom >= target.scrollHeight - SCROLL_THRESHOLD
      if (hasMore && nearBottom && fetchList) {
        fetchList({ pageNo: (pageNo || 1) + 1, search: searchVal })
      }
    }, 300),
    [],
  )

  const onSelectChange = (_, option) => {
    form.setFieldValue(labelKeyFieldName, option?.label)
    onModelSelect?.(option?.model)
  }

  const modifyForOptions = () => {
    const newList = []
    const isSearchList = search?.trim()
    list?.forEach(model => {
      const { name, id } = model
      if (isEqual(selectedOption, id) && !isSearchList) return
      newList.push({
        label: name,
        value: id,
        model,
      })
    })

    return selectedOption && !isSearchList
      ? [
          {
            label: form.getFieldValue(labelKeyFieldName),
            value: selectedOption,
            model: list?.find(({ id }) => isEqual(id, selectedOption)),
          },
          ...newList,
        ]
      : newList
  }

  const handleAddFn = async () => {
    const searchValue = search?.trim()
    const newValue = await addOption(searchValue, fieldName)
    if (newValue) {
      setSearch('')
      getVehicleModelList({
        search: '',
        newValue,
      })
    }
  }

  return (
    <ANTDSelect
      {...restProps}
      filterOption={false}
      options={modifyForOptions()}
      onSelect={onSelectChange} //this will not break 'form' onChange
      onPopupScroll={onScroll}
      dropdownRender={menu => (
        <>
          <div
            style={{
              padding: '5px 4px',
            }}
            className="d-flex"
          >
            <ANTDInput
              style={{ marginRight: '8px' }}
              placeholder={t('job_SearchModel')}
              value={search}
              onChange={onInputSearch}
              onKeyDown={e => e.stopPropagation()}
            />
            {addOption && (
              <ANTDButton
                type="primary"
                onClick={handleAddFn}
                className="ml-5"
                disabled={!search?.trim()}
              >
                {t('btn_Add')}
              </ANTDButton>
            )}
          </div>
          <ANTDDivider
            style={{
              margin: '8px 0',
            }}
          />
          {menu}
          {loader && <ANTDSpin size="small" style={{ paddingLeft: 10 }} />}
        </>
      )}
    />
  )
}

export default VehicleModelSelector
